import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select } from 'antd';
import { motion } from 'framer-motion';
import { AlertTriangle, ChevronRight, GitBranch } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';
import type { Role } from '../../../../../types/role';
import type { RoleEditorFormValues } from '../types';

/**
 * Step 4 of 6 — Approval chain.
 *
 * Hidden entirely for mobile-user roles. For every other role this section
 * owns three sub-blocks: (1) approver-role picker + animated chain preview,
 * (2) "requires approval for" multiselect, (3) plain-English summary card.
 */
export default function ApprovalChainSection() {
  const {
    control,
    watch,
    formState: { errors: _errors },
  } = useFormContext<RoleEditorFormValues>();

  const {
    isMobileUser,
    approverRoles,
    selectedApproverRole,
    setSelectedApproverRole,
    selectedRoleType,
    approvalChain,
    editingRole,
    activeDept,
    isReadonly,
  } = useRoleEditorContext();

  // Hard hide for mobile-app roles — they never participate in approvals.
  if (isMobileUser) return null;

  const watchedName = watch('name');
  const requiresApprovalFor = watch('requiresApprovalFor') ?? [];

  const needsApprover =
    selectedRoleType === 'requester' || selectedRoleType === 'both';

  // Format approver role options once. Ant Design's Select takes labels via
  // its `options` prop; we add the dept badge inline in `optionRender`.
  const approverOptions = useMemo(
    () =>
      approverRoles.map((r) => ({
        value: (r._id || r.id) as string,
        label: r.name,
        role: r,
      })),
    [approverRoles],
  );

  // Build the plain-English summary lines once.
  const summaryLines = useMemo(() => {
    const lines: string[] = [];
    if (requiresApprovalFor.length > 0) {
      lines.push(
        `This role approves submissions from ${requiresApprovalFor.length} other role${
          requiresApprovalFor.length === 1 ? '' : 's'
        }.`,
      );
    }
    if (
      (selectedRoleType === 'requester' || selectedRoleType === 'both') &&
      approvalChain.steps.length > 0 &&
      !approvalChain.cycle
    ) {
      const chain = approvalChain.steps.map((s) => s.name).join(' → ');
      lines.push(`Submissions from this role go to: ${chain}.`);
    }
    if (selectedRoleType === 'approver' && requiresApprovalFor.length === 0) {
      lines.push('This role is an approver but no requester roles target it yet.');
    }
    if (
      (selectedRoleType === 'requester' || selectedRoleType === 'both') &&
      !selectedApproverRole
    ) {
      lines.push('No approver picked yet — submissions from this role will block.');
    }
    return lines;
  }, [
    requiresApprovalFor,
    selectedRoleType,
    selectedApproverRole,
    approvalChain.cycle,
    approvalChain.steps,
  ]);

  const renderDeptBadge = (dept?: string) => (
    <span
      className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{
        backgroundColor: 'rgba(31,90,71,0.10)',
        color: SOVEREIGN.bottle,
      }}
    >
      {dept ?? activeDept}
    </span>
  );

  return (
    <SectionCard
      icon={GitBranch}
      title="Approval chain"
      sub="Who approves submissions from this role, and which roles this role approves."
      step={4}
      totalSteps={6}
    >
      <div className="space-y-5">
        {/* ── Block 1: Approver role picker + chain preview ───────────── */}
        {needsApprover && (
          <div className="space-y-3">
            <div>
              <label
                className="block text-[12px] font-medium mb-1.5"
                style={{ color: SOVEREIGN.ink }}
              >
                Who approves submissions from this role?
              </label>
              <Select
                value={selectedApproverRole || undefined}
                onChange={(val) => setSelectedApproverRole((val as string) || '')}
                options={approverOptions}
                placeholder="Pick an approver role…"
                disabled={isReadonly}
                allowClear
                showSearch
                optionFilterProp="label"
                size="large"
                style={{ width: '100%' }}
                optionRender={(opt) => {
                  const role = (opt.data as { role?: Role }).role;
                  return (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm" style={{ color: SOVEREIGN.ink }}>{role?.name}</span>
                      {renderDeptBadge(role?.department)}
                    </div>
                  );
                }}
              />
              <p
                className="mt-1 text-[11px]"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                Multi-level chains resolve automatically — pick the direct approver only.
              </p>
            </div>

            {/* Cycle banner */}
            {approvalChain.cycle && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-600" />
                <p className="text-[12px] text-red-800">
                  Circular approval mapping. Pick an approver whose own chain doesn't
                  lead back to this role.
                </p>
              </div>
            )}

            {/* Terminal-not-reached banner */}
            {!approvalChain.cycle &&
              !approvalChain.terminalReached &&
              approvalChain.steps.length > 0 && (
                <div
                  className="flex items-start gap-2 rounded-lg px-3 py-2"
                  style={{
                    border: `1px solid ${SOVEREIGN.champagne}`,
                    background: 'rgba(201,169,97,0.08)',
                  }}
                >
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: SOVEREIGN.champagne }} />
                  <p className="text-[12px]" style={{ color: SOVEREIGN.bottle }}>
                    Approver's chain doesn't terminate at a top-level role. Confirm
                    this is intentional.
                  </p>
                </div>
              )}

            {/* Chain preview */}
            {selectedApproverRole && approvalChain.steps.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-3"
                style={{
                  border: `1px solid ${SOVEREIGN.innerRule}`,
                  background: SOVEREIGN.inputBg,
                }}
              >
                {/* Step 0 — this role */}
                <span
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ring-1"
                  style={{
                    backgroundColor: SOVEREIGN.paper,
                    color: SOVEREIGN.ink,
                    boxShadow: `inset 0 0 0 1px ${SOVEREIGN.innerRule}`,
                  }}
                >
                  {watchedName?.trim() || editingRole?.name || 'This role'}
                  {renderDeptBadge(editingRole?.department ?? activeDept)}
                </span>

                {approvalChain.steps.map((step, i) => {
                  const stepId = step._id || step.id || `step-${i}`;
                  const cycledHere =
                    approvalChain.cycle && i === approvalChain.steps.length - 1;
                  return (
                    <motion.div
                      key={stepId}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08, duration: 0.32 }}
                      className="inline-flex items-center gap-1.5"
                    >
                      <ChevronRight size={14} style={{ color: SOVEREIGN.barTrack }} />
                      <span
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ring-1"
                        style={{
                          backgroundColor: cycledHere
                            ? '#fef2f2'
                            : 'rgba(31,90,71,0.08)',
                          color: cycledHere ? '#991b1b' : SOVEREIGN.bottle,
                          boxShadow: `inset 0 0 0 1px ${
                            cycledHere ? '#fecaca' : SOVEREIGN.innerRule
                          }`,
                        }}
                      >
                        {step.name}
                        {renderDeptBadge(step.department)}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Block 2: Requires approval for ──────────────────────────── */}
        <div className="pt-5" style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }}>
          <label
            className="block text-[12px] font-medium mb-1.5"
            style={{ color: SOVEREIGN.ink }}
          >
            Which roles' work does this role approve?
          </label>
          <Controller
            name="requiresApprovalFor"
            control={control}
            render={({ field }) => (
              <Select
                mode="multiple"
                value={(field.value as string[] | undefined) ?? []}
                onChange={(val) => field.onChange(val)}
                onBlur={field.onBlur}
                options={approverOptions}
                placeholder="Pick roles whose submissions this role signs off on…"
                disabled={isReadonly}
                allowClear
                showSearch
                optionFilterProp="label"
                size="large"
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
            )}
          />
          <p
            className="mt-1 text-[11px]"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Submissions from these roles will require this role's sign-off.
          </p>
        </div>

        {/* ── Block 3: Plain-English summary ──────────────────────────── */}
        <div className="pt-5" style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }}>
          <div
            className="rounded-xl px-4 py-3"
            style={{
              background: 'rgba(31,90,71,0.06)',
              border: `1px solid ${SOVEREIGN.cardBorder}`,
            }}
          >
            <p
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              What this means
            </p>
            {summaryLines.length === 0 ? (
              <p
                className="mt-1 text-[12px]"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                Pick a role type and approver to see the chain in plain English.
              </p>
            ) : (
              <ul className="mt-1.5 space-y-1">
                {summaryLines.map((line) => (
                  <li
                    key={line}
                    className="text-[12.5px] leading-snug"
                    style={{ color: SOVEREIGN.bottle }}
                  >
                    • {line}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
